import { EarningsPeriod } from '@api';
import { Chip } from '@design/components/chip/Chip';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownEntry } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { DropdownRadioGroup } from '@design/components/dropdown-menu/dropdown-radio-group/DropdownRadioGroup';
import { isBefore, subMonths } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ReportingDates.module.scss';

export type ReportingPeriod = Pick<
  EarningsPeriod,
  'fiscal_period' | 'fiscal_year' | 'reporting_date'
>;

export type ReportingDatesProps<T extends ReportingPeriod> = {
  reportingPeriods: T[];
  activeReportingPeriod: T;
  onActiveReportingPeriodChange: (reportingPeriod: T) => void;
};

const getReportingPeriodLabel = ({
  fiscal_period,
  fiscal_year,
}: ReportingPeriod) => `${fiscal_period} ${fiscal_year}`;

/**
 * `reportingPeriods` is sorted from soonest/most-recent to oldest, so the
 * first item is the upcoming (or just-passed) report. Defaults to that
 * period once its reporting date is a month or less away, otherwise falls
 * back to the most recently reported quarter.
 */
export const getDefaultActiveReportingPeriod = (
  reportingPeriods: EarningsPeriod[]
): EarningsPeriod => {
  const [nextReportingPeriod] = reportingPeriods;

  const isWithinAMonthOfNextReport =
    !!nextReportingPeriod &&
    isBefore(
      subMonths(new Date(nextReportingPeriod.reporting_date), 1),
      new Date()
    );

  if (isWithinAMonthOfNextReport) {
    return nextReportingPeriod;
  }

  const lastReportedPeriod = reportingPeriods.find(period => period.reported);

  return lastReportedPeriod ?? nextReportingPeriod;
};

export const ReportingDates = <T extends ReportingPeriod>({
  reportingPeriods,
  activeReportingPeriod,
  onActiveReportingPeriodChange,
}: ReportingDatesProps<T>) => {
  const { t } = useTranslation(['earnings']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const periodItems: DropdownEntry[] = reportingPeriods.map(
    reportingPeriod => ({
      key: reportingPeriod.reporting_date,
      label: getReportingPeriodLabel(reportingPeriod),
      onClick: () => {
        onActiveReportingPeriodChange(reportingPeriod);
        setIsDropdownOpen(false);
      },
    })
  );

  return (
    <div className={styles.root} data-testid="reporting-dates">
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        className={styles.menuWrapper}
        triggerNode={
          <Chip
            className={styles.chip}
            label={
              <div className={styles.labelContainer}>
                <span className={styles.label}>
                  {t('earnings:reportingDates.label')}
                </span>
                <span className={styles.activePeriod}>
                  {getReportingPeriodLabel(activeReportingPeriod)}
                </span>
              </div>
            }
            rightIcon="ChevronLightDown"
            rightIconSize={12}
            data-testid="reporting-dates-chip"
          />
        }
      >
        <DropdownRadioGroup
          items={periodItems}
          selectedKey={activeReportingPeriod.reporting_date}
        />
      </DropdownMenu>
    </div>
  );
};
